import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Alert, ActivityIndicator } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRecipe, useUpdateRecipe } from '@/hooks/useRecipes';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { RecipeImagePicker, type SelectedRecipeImage } from '@/components/RecipeImagePicker';
import { RECIPE_PLACEHOLDER_KEYS } from '@/constants/recipePlaceholders';
import { Brand, Neutrals } from '@/constants/theme';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';

export default function EditRecipe() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { id } = useLocalSearchParams<{ id: string }>();
  const recipeId = typeof id === 'string' ? id : Array.isArray(id) ? id[0] : undefined;
  const { recipe, loading: recipeLoading, error } = useRecipe(recipeId);
  const { updateRecipe, loading: saveLoading } = useUpdateRecipe();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [servings, setServings] = useState('4');
  const [prepTime, setPrepTime] = useState('');
  const [cookTime, setCookTime] = useState('');
  const [ingredients, setIngredients] = useState<string[]>(['']);
  const [instructions, setInstructions] = useState<string[]>(['']);
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [selectedImage, setSelectedImage] = useState<SelectedRecipeImage>(null);

  useEffect(() => {
    if (recipe) {
      setTitle(recipe.title);
      setDescription(recipe.description ?? '');
      setServings(recipe.servings.toString());
      setPrepTime(recipe.prepTime ? recipe.prepTime.toString() : '');
      setCookTime(recipe.cookTime ? recipe.cookTime.toString() : '');
      setIngredients(recipe.ingredients.length > 0 ? recipe.ingredients : ['']);
      setInstructions(recipe.instructions.length > 0 ? recipe.instructions : ['']);
      setTags(recipe.tags ?? []);
      const firstImage = recipe.images?.[0];
      setSelectedImage(
        firstImage && RECIPE_PLACEHOLDER_KEYS.includes(firstImage as typeof RECIPE_PLACEHOLDER_KEYS[number])
          ? (firstImage as SelectedRecipeImage)
          : null
      );
    }
  }, [recipe]);

  const handleAddIngredient = () => {
    setIngredients([...ingredients, '']);
  };

  const handleRemoveIngredient = (index: number) => {
    setIngredients(ingredients.filter((_, i) => i !== index));
  };

  const handleIngredientChange = (index: number, value: string) => {
    const newIngredients = [...ingredients];
    newIngredients[index] = value;
    setIngredients(newIngredients);
  };

  const handleAddInstruction = () => {
    setInstructions([...instructions, '']);
  };

  const handleRemoveInstruction = (index: number) => {
    setInstructions(instructions.filter((_, i) => i !== index));
  };

  const handleInstructionChange = (index: number, value: string) => {
    const newInstructions = [...instructions];
    newInstructions[index] = value;
    setInstructions(newInstructions);
  };

  const handleAddTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setTags(tags.filter(t => t !== tag));
  };

  const handleSubmit = async () => {
    if (!recipeId) return;
    if (!title.trim()) {
      Alert.alert('Error', 'Please enter a recipe title');
      return;
    }

    const filteredIngredients = ingredients.filter(i => i.trim());
    const filteredInstructions = instructions.filter(i => i.trim());

    if (filteredIngredients.length === 0) {
      Alert.alert('Error', 'Please add at least one ingredient');
      return;
    }

    if (filteredInstructions.length === 0) {
      Alert.alert('Error', 'Please add at least one instruction');
      return;
    }

    try {
      await updateRecipe(recipeId, {
        title: title.trim(),
        description: description.trim() || undefined,
        ingredients: filteredIngredients,
        instructions: filteredInstructions,
        servings: parseInt(servings) || 4,
        prepTime: parseInt(prepTime) || 0,
        cookTime: parseInt(cookTime) || 0,
        images: selectedImage ? [selectedImage] : [],
        tags,
      });
      router.back();
    } catch {
      Alert.alert('Error', 'Failed to save recipe. Please try again.');
    }
  };

  if (recipeLoading || !recipe) {
    return (
      <View style={[styles.container, styles.centered]}>
        <LinearGradient
          colors={[...Brand.primaryGradient]}
          style={styles.background}
        />
        {recipeLoading ? (
          <ActivityIndicator size="large" color={Brand.primary} />
        ) : (
          <>
            <Text style={styles.errorText}>{error ? 'Failed to load recipe' : 'Recipe not found'}</Text>
            <Button onPress={() => router.back()}>Back</Button>
          </>
        )}
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <LinearGradient
        colors={[...Brand.primaryGradient]}
        style={styles.background}
      />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[styles.content, { paddingTop: 16 + insets.top }]}
      >
        <View style={styles.header}>
          <TouchableOpacity onPress={() => router.back()} style={styles.backButton}>
            <Ionicons name="arrow-back" size={20} color={Brand.primary} />
            <Text style={styles.backText}>Back</Text>
          </TouchableOpacity>
          <Text style={styles.title}>Edit Recipe</Text>
        </View>

        {/* Basic Info */}
        <Card style={styles.card}>
          <CardHeader>
            <CardTitle>
              <Text style={styles.cardTitleText}>Basic Information</Text>
            </CardTitle>
          </CardHeader>
          <CardContent style={styles.cardContent}>
            <RecipeImagePicker value={selectedImage} onChange={setSelectedImage} />

            <Input
              label="Recipe Title *"
              value={title}
              onChangeText={setTitle}
              placeholder="e.g., Thai Green Curry"
            />

            <Input
              label="Description"
              value={description}
              onChangeText={setDescription}
              placeholder="Brief description of your recipe"
              multiline
              numberOfLines={3}
            />

            <View style={styles.row}>
              <View style={styles.rowItem}>
                <Input
                  label="Servings"
                  value={servings}
                  onChangeText={setServings}
                  keyboardType="numeric"
                />
              </View>
              <View style={styles.rowItem}>
                <Input
                  label="Prep Time (min)"
                  value={prepTime}
                  onChangeText={setPrepTime}
                  keyboardType="numeric"
                  placeholder="15"
                />
              </View>
              <View style={styles.rowItem}>
                <Input
                  label="Cook Time (min)"
                  value={cookTime}
                  onChangeText={setCookTime}
                  keyboardType="numeric"
                  placeholder="30"
                />
              </View>
            </View>
          </CardContent>
        </Card>

        {/* Ingredients */}
        <Card style={styles.card}>
          <CardHeader>
            <CardTitle>
              <Text style={styles.cardTitleText}>Ingredients</Text>
            </CardTitle>
          </CardHeader>
          <CardContent style={styles.cardContent}>
            {ingredients.map((ingredient, index) => (
              <View key={index} style={styles.listItem}>
                <Input
                  value={ingredient}
                  onChangeText={(value) => handleIngredientChange(index, value)}
                  placeholder="e.g., 2 cups flour"
                  containerStyle={styles.listItemInput}
                />
                {ingredients.length > 1 && (
                  <TouchableOpacity
                    onPress={() => handleRemoveIngredient(index)}
                    style={styles.removeButton}
                  >
                    <Ionicons name="close-circle" size={24} color={Brand.primaryDark} />
                  </TouchableOpacity>
                )}
              </View>
            ))}
            <TouchableOpacity onPress={handleAddIngredient} style={styles.addButton}>
              <Ionicons name="add" size={16} color={Brand.primary} />
              <Text style={styles.addButtonText}>Add Ingredient</Text>
            </TouchableOpacity>
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card style={styles.card}>
          <CardHeader>
            <CardTitle>
              <Text style={styles.cardTitleText}>Instructions</Text>
            </CardTitle>
          </CardHeader>
          <CardContent style={styles.cardContent}>
            {instructions.map((instruction, index) => (
              <View key={index} style={styles.instructionItem}>
                <View style={styles.instructionNumber}>
                  <Text style={styles.instructionNumberText}>{index + 1}</Text>
                </View>
                <Input
                  value={instruction}
                  onChangeText={(value) => handleInstructionChange(index, value)}
                  placeholder="Describe this step..."
                  multiline
                  numberOfLines={2}
                  containerStyle={styles.instructionInput}
                />
                {instructions.length > 1 && (
                  <TouchableOpacity
                    onPress={() => handleRemoveInstruction(index)}
                    style={styles.removeButton}
                  >
                    <Ionicons name="close-circle" size={24} color={Brand.primaryDark} />
                  </TouchableOpacity>
                )}
              </View>
            ))}
            <TouchableOpacity onPress={handleAddInstruction} style={styles.addButton}>
              <Ionicons name="add" size={16} color={Brand.primary} />
              <Text style={styles.addButtonText}>Add Step</Text>
            </TouchableOpacity>
          </CardContent>
        </Card>

        {/* Tags */}
        <Card style={styles.card}>
          <CardHeader>
            <CardTitle>
              <Text style={styles.cardTitleText}>Tags</Text>
            </CardTitle>
          </CardHeader>
          <CardContent style={styles.cardContent}>
            <View style={styles.tagInputRow}>
              <Input
                value={newTag}
                onChangeText={setNewTag}
                placeholder="Add tag (e.g., Vegetarian, Italian)"
                onSubmitEditing={handleAddTag}
                containerStyle={styles.tagInputContainer}
                style={styles.tagInput}
              />
              <Button onPress={handleAddTag} style={styles.tagAddButton}>
                Add
              </Button>
            </View>
            {tags.length > 0 && (
              <View style={styles.tagsContainer}>
                {tags.map(tag => (
                  <View key={tag} style={styles.tagPill}>
                    <Text style={styles.tagText}>{tag}</Text>
                    <TouchableOpacity
                      onPress={() => handleRemoveTag(tag)}
                      style={styles.tagRemove}
                      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                    >
                      <Ionicons name="close" size={12} color={Brand.primaryDark} />
                    </TouchableOpacity>
                  </View>
                ))}
              </View>
            )}
          </CardContent>
        </Card>

        {/* Submit */}
        <View style={styles.submitRow}>
          <Button variant="outline" onPress={() => router.back()} style={styles.submitButton} disabled={saveLoading}>
            Cancel
          </Button>
          <Button onPress={handleSubmit} style={styles.submitButton} loading={saveLoading} disabled={saveLoading}>
            Save Changes
          </Button>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centered: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  background: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  },
  scrollView: {
    flex: 1,
  },
  content: {
    paddingHorizontal: 16,
    paddingBottom: 100,
    maxWidth: 864,
    alignSelf: 'center',
    width: '100%',
  },
  header: {
    marginBottom: 24,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 6,
    marginBottom: 12,
    paddingVertical: 4,
  },
  backText: {
    color: Brand.primary,
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 20,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
  },
  card: {
    marginBottom: 16,
  },
  cardTitleText: {
    fontSize: 20,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
  },
  cardContent: {
    gap: 16,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 12,
  },
  rowItem: {
    flex: 1,
  },
  listItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
    width: '100%',
  },
  listItemInput: {
    flex: 1,
    minWidth: 0,
    marginBottom: 0,
  },
  removeButton: {
    padding: 4,
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    width: '100%',
    backgroundColor: 'transparent',
    borderWidth: 2,
    borderColor: Brand.primary,
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  addButtonText: {
    color: Brand.primary,
    fontSize: 16,
    fontWeight: '600',
  },
  instructionItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 12,
    width: '100%',
  },
  instructionNumber: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: Neutrals.surface,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 8,
  },
  instructionNumberText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
  },
  instructionInput: {
    flex: 1,
    minWidth: 0,
    marginBottom: 0,
  },
  tagInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  tagInputContainer: {
    flex: 1,
    minWidth: 0,
    marginBottom: 0,
  },
  tagInput: {
    flex: 1,
  },
  tagAddButton: {
    minHeight: 48,
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  tagPill: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: Brand.primaryLight,
  },
  tagText: {
    fontSize: 12,
    fontWeight: '600',
    color: Brand.primaryDark,
  },
  tagRemove: {
    padding: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  submitRow: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 24,
  },
  submitButton: {
    flex: 1,
  },
  errorText: {
    fontSize: 16,
    color: Neutrals.textSecondary,
    marginBottom: 16,
  },
});
